import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminSortingCriteriaService, {
    CreatePayload,
    DeletePayload,
    UpdatePayload,
    UpdateValuePayload,
} from '../services/AdminSortingCriteriaService';
import CriterionType from '../types/criteria/CriterionType';
import CriterionValueType from '../types/criteria/CriterionValueType';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ImportStates, { ImportState } from '../constants/ImportStates';
import CriteriaImportPreview from '../types/criteria/CriteriaImportPreview';
import ImportProcess from '../../common/types/ImportProcess';
import ImportService from '../services/ImportService';
import {
    createAppAsyncThunk,
    createNotifiedThunk,
} from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminSortingCriteria';

const getCriteria = createNotifiedThunk(
    `${NAME}/getCriteria`,
    AdminSortingCriteriaService.getCriteria,
    { error: 'fe.admin.error.load-criteria' },
);
const getCriterion = createNotifiedThunk(
    `${NAME}/getCriterion`,
    AdminSortingCriteriaService.getCriterion,
    { error: 'fe.admin.error.load-criterion' },
);
const updateCriterion = createAppAsyncThunk<
    RequestServiceResponse<CriterionType>,
    UpdatePayload
>(`${NAME}/updateCriterion`, async (payload, { dispatch }) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.updateCriterion(payload);

    if (parentCriterionId) {
        const { projectId } = payload;
        dispatch(
            getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    }

    return result;
});
const moveCriterion = createAppAsyncThunk<
    RequestServiceResponse<CriterionType>,
    UpdatePayload
>(`${NAME}/moveCriterion`, async (payload, { dispatch }) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.updateCriterion(payload);
    const { projectId } = payload;
    if (parentCriterionId) {
        dispatch(
            getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    } else {
        dispatch(getCriteria({ projectId }));
    }

    return result;
});
const createCriterion = createAppAsyncThunk<
    RequestServiceResponse<CriterionType>,
    CreatePayload
>(`${NAME}/createCriterion`, async (payload, { dispatch }) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.createCriterion(payload);

    if (parentCriterionId) {
        const { projectId, parentCriterionValue } = payload;
        dispatch(
            getValues({
                projectId,
                criterionId: parentCriterionId,
                parentCriterionValueId: parentCriterionValue?.id,
            }),
        );
    }

    return result;
});
const deleteCriterion = createAppAsyncThunk<
    RequestServiceResponse<CriterionType>,
    DeletePayload
>(`${NAME}/deleteCriterion`, async (payload, { dispatch }) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.deleteCriterion(payload);

    if (parentCriterionId) {
        const { projectId } = payload;
        dispatch(
            getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    }

    return result;
});

const getValues = createNotifiedThunk(
    `${NAME}/getValues`,
    AdminSortingCriteriaService.getValues,
    { error: 'fe.admin.error.load-values' },
);
const updateValue = createNotifiedThunk(
    `${NAME}/updateValue`,
    AdminSortingCriteriaService.updateValue,
    { error: 'fe.admin.error.update-value' },
);
const moveValue = createAppAsyncThunk<
    RequestServiceResponse<CriterionValueType>,
    UpdateValuePayload
>(`${NAME}/moveValue`, async (payload, { dispatch }) => {
    const result = await AdminSortingCriteriaService.updateValue(payload);
    const { projectId, criterionId } = payload;

    dispatch(getCriterion({ projectId, id: criterionId }));

    return result;
});
const createValue = createNotifiedThunk(
    `${NAME}/createValue`,
    AdminSortingCriteriaService.createValue,
    { error: 'fe.admin.error.create-value' },
);
const deleteValue = createNotifiedThunk(
    `${NAME}/deleteValue`,
    AdminSortingCriteriaService.deleteValue,
    { error: 'fe.admin.error.delete-value' },
);

const importCriteria = createNotifiedThunk(
    `${NAME}/importCriteria`,
    AdminSortingCriteriaService.importCriteria,
    { error: 'fe.admin.error.import-criteria' },
);
const confirmImport = createNotifiedThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
    { error: 'fe.admin.error.import-file' },
);
const cancelImport = createNotifiedThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
    { error: 'fe.admin.error.cancel-import' },
);
const getImportProgress = createNotifiedThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
    { error: 'fe.admin.error.import-users' },
);
const exportCriteria = createNotifiedThunk(
    `${NAME}/exportCriteria`,
    AdminSortingCriteriaService.exportCriteria,
    { error: 'fe.admin.error.import-criteria' },
);

export interface AdminSortingCriteriaState {
    criteria: IBaseState<CriterionType[]>;
    import: {
        loading: boolean;
        preview: CriteriaImportPreview | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
        projectId: number | null;
    };
}

const initialState: AdminSortingCriteriaState = {
    criteria: {
        data: [],
        loading: false,
    },
    import: {
        loading: false,
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
        projectId: null,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport,
    },
    extraReducers: (builder) => {
        builder.addCase(
            getCriteria.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (action.payload.data) {
                    state.criteria.data = action.payload.data;
                }
            },
        );
        // builder.addCase(getCriterion.fulfilled, (state:AdminSortingCriteriaState, action) => {
        //     if (!action.payload.data) {
        //         return;
        //     }
        //
        //     const criterion = findCriterionById();
        //
        //
        // })
        builder.addCase(
            createCriterion.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const data = action.payload.data;

                const { parentCriterionValueId } = action.meta.arg;
                if (!parentCriterionValueId) {
                    state.criteria.data.push(data);
                    return;
                }

                // const [value] = findValueById(
                //     state.data,
                //     parentCriterionValueId,
                // );
                // if (!value) {
                //     return;
                // }
                //
                // if (!value.criteria) {
                //     value.criteria = [];
                // }
                //
                // value.criteria.push(data);
            },
        );
        builder.addCase(
            updateCriterion.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { id, parentCriterionId } = action.meta.arg;

                if (parentCriterionId) {
                    return;
                }

                const criterion = AdminSortingCriteriaService.findCriterionById(
                    state.criteria.data,
                    id,
                );
                if (!criterion) {
                    return;
                }

                const { values, name, mandatory, displayOption, order } =
                    action.payload.data;

                criterion.values = values;
                criterion.name = name;
                criterion.mandatory = mandatory;
                criterion.displayOption = displayOption;
                criterion.order = order;
            },
        );
        // builder.addCase(
        //     deleteCriterion.fulfilled,
        //     (state: AdminSortingCriteriaState, action) => {
        //         const { id } = action.meta.arg;
        //
        //         let [criterion, criteria, index] = findCriterionById(
        //             state.data,
        //             id,
        //         );
        //         if (!criterion || !criteria.length) {
        //             return;
        //         }
        //
        //         criteria.splice(index, 1);
        //     },
        // );

        builder.addCase(
            getValues.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { criterionId, parentCriterionValueId } = action.meta.arg;
                if (!criterionId) {
                    return;
                }

                const criterion = AdminSortingCriteriaService.findCriterionById(
                    state.criteria.data,
                    criterionId,
                    parentCriterionValueId,
                );
                if (!criterion) {
                    return;
                }

                criterion.values = action.payload.data;
            },
        );
        builder.addCase(
            createValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { criterionId, parentCriterionValueId } = action.meta.arg;
                if (!criterionId) {
                    return;
                }
                const criterion = AdminSortingCriteriaService.findCriterionById(
                    state.criteria.data,
                    criterionId,
                    parentCriterionValueId,
                );
                if (!criterion) {
                    return;
                }

                if (!criterion.values) {
                    criterion.values = [];
                }

                criterion.values.push(action.payload.data);
            },
        );
        builder.addCase(
            updateValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { id } = action.meta.arg;

                const [criterionValue] = findValueById(state.criteria.data, id);
                if (!criterionValue) {
                    return;
                }

                const { value, criteria, order } = action.payload.data;

                criterionValue.value = value;
                criterionValue.criteria = criteria;
                criterionValue.order = order;
            },
        );
        builder.addCase(
            deleteValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                const { id } = action.meta.arg;

                const [value, values, index] = findValueById(
                    state.criteria.data,
                    id,
                );
                if (!value || !values.length) {
                    return;
                }

                values.splice(index, 1);
            },
        );

        builder.addCase(
            importCriteria.pending,
            (state: AdminSortingCriteriaState, action) => {
                resetImport(state);
                state.import.state = ImportStates.Pending;
                state.import.projectId = action.meta.arg.projectId;
            },
        );
        builder.addCase(
            importCriteria.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                const data = action.payload.data as {
                    importId: number;
                    preview: CriteriaImportPreview;
                };
                state.import.state = ImportStates.Preview;
                state.import.importId = data.importId;
                state.import.preview = data.preview;
            },
        );
        builder.addCase(
            importCriteria.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
                state.import.state = ImportStates.Fail;
            },
        );
        builder.addCase(
            confirmImport.pending,
            (state: AdminSortingCriteriaState) => {
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            confirmImport.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data as ImportProcess;
                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            confirmImport.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
            },
        );
        builder.addCase(
            cancelImport.pending,
            (state: AdminSortingCriteriaState) => {
                state.import.state = ImportStates.Idle;
                state.import.importId = null;
            },
        );
        builder.addCase(
            getImportProgress.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                const data = action.payload.data as ImportProcess;

                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
            },
        );
    },
});

function resetImport(state: AdminSortingCriteriaState) {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
    state.import.projectId = null;
}

function findValueById(
    criteria: CriterionType[],
    id: number,
): [CriterionValueType | null, CriterionValueType[], number] {
    const result: [CriterionValueType | null, CriterionValueType[], number] = [
        null,
        [],
        -1,
    ];

    return find(criteria, id, result);
    function find(
        criteria: CriterionType[],
        id: number,
        result: [CriterionValueType | null, CriterionValueType[], number],
    ) {
        for (const criterion of criteria) {
            if (result[0]) {
                break;
            }

            if (!criterion?.values?.length) {
                continue;
            }

            const values = criterion.values;
            for (let index = 0; index < values.length; index++) {
                if (result[0]) {
                    break;
                }

                const value = values[index];
                if (value.id === id) {
                    result = [value, criterion.values, index];
                    break;
                }

                if (!value?.criteria?.length) {
                    continue;
                }

                result = findValueById(value.criteria, id);
            }
        }

        return result;
    }
}

const state = (state: AppState): AdminSortingCriteriaState =>
    state.admin.criteria;
const all = state;
const AdminSortingCriteriaSlice = {
    reducer: slice.reducer,
    actions: {
        ...slice.actions,
        getCriteria,
        getCriterion,
        updateCriterion,
        createCriterion,
        deleteCriterion,
        moveCriterion,
        getValues,
        updateValue,
        createValue,
        deleteValue,
        moveValue,
        importCriteria,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportCriteria,
    },
    state: {
        all,
        criteria: createSelector(
            all,
            (state: AdminSortingCriteriaState) => state.criteria,
        ),
        import: createSelector(
            all,
            (state: AdminSortingCriteriaState) => state.import,
        ),
    },
};

export default AdminSortingCriteriaSlice;
